import { useState } from 'react';
import { PlusIcon, TrashIcon, UsersIcon } from '@phosphor-icons/react';
import { useTranslation } from 'contexts/translation-context';
import { useSupplier } from './supplierContext';
import { Card } from 'components/Card';
import { EmptyState } from 'components/empty-state/EmptyState';
import { Pill, type PillColor } from 'components/pill/Pill';
import { UserIdentityCell } from 'components/user/UserIdentityCell';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import { ITableHeader, Table } from 'components/table/Table';
import { IterableElement } from 'type-fest';
import { deleteCarrierUserInvite, type SupplierDetailEntry } from '../../models';
import { getDisplayError } from '../../../../utils/get-display-error';
import { captureException } from '@sentry/react';
import { LinkButton } from '../../../../components/button/ButtonLink';
import toast from 'react-hot-toast';
import { SimpleSelect, ISimpleSelectItem } from '../../../../components/select/SimpleSelect';
import { Input } from '../../../../components/input/Input';
import { updateUserSupplier } from '../../../user/models';

export type SupplierUserInvite = IterableElement<SupplierDetailEntry['invitedUsers']>;
export type SupplierPortalUser = IterableElement<SupplierDetailEntry['portalUsers']>;

const getRoleDisplayName = (role: string): string => {
  switch (role) {
    case 'CarrierDispatcher':
      return 'Dispatcher';
    case 'CarrierDriver':
      return 'Driver';
    default:
      return role;
  }
};

/**
 * An invitation is the same row as an account with a different status, so both
 * live in one table rather than in two separate ones.
 */
interface IAccessRow {
  id: string;
  portalUser: SupplierPortalUser | null;
  invite: SupplierUserInvite | null;
}

export const SupplierUsersPage = () => {
  const { supplier, refreshData } = useSupplier();
  const { i18n } = useTranslation();
  const [editingNicknameId, setEditingNicknameId] = useState<number | null>(null);
  const [nicknameValue, setNicknameValue] = useState('');

  const roleOptions: ISimpleSelectItem[] = [
    { key: 'CarrierDispatcher', name: 'Dispatcher' },
    { key: 'CarrierDriver', name: 'Chauffeur' },
  ];

  const handleRoleChange = async (userSupplierId: number, userName: string, newRole: string) => {
    try {
      await updateUserSupplier(userSupplierId, {
        role: newRole as 'CarrierDispatcher' | 'CarrierDriver',
      });
      toast.success(`Rol van ${userName} bijgewerkt naar ${getRoleDisplayName(newRole)}`);
      refreshData();
    } catch (err) {
      captureException(err);
      toast.error(getDisplayError(err));
    }
  };

  const handleNicknameSubmit = async (userSupplierId: number) => {
    try {
      const trimmed = nicknameValue.trim();
      await updateUserSupplier(userSupplierId, { nickname: trimmed || null });
      toast.success('Bijnaam bijgewerkt');
      setEditingNicknameId(null);
      refreshData();
    } catch (err) {
      captureException(err);
      toast.error(getDisplayError(err));
    }
  };

  const accessHeaders: ITableHeader[] = [
    { id: 'name', name: i18n('carrierPortal.users.name'), width: '240px' },
    { id: 'email', name: i18n('carrierPortal.users.email') },
    { id: 'status', name: 'Status', width: '130px' },
    { id: 'nickname', name: 'Bijnaam', width: '200px' },
    { id: 'role', name: i18n('carrierPortal.users.role'), width: '200px' },
    { id: 'actions', name: '', width: '60px', align: 'right' },
  ];

  const accessRows: IAccessRow[] = [
    ...supplier.portalUsers.map((portalUser) => ({ id: `user-${portalUser.id}`, portalUser, invite: null })),
    ...supplier.invitedUsers.map((invite) => ({ id: `invite-${invite.id}`, portalUser: null, invite })),
  ];

  const invitedCount = supplier.invitedUsers.length;
  const meta = `${supplier.portalUsers.length} ${supplier.portalUsers.length === 1 ? 'account' : 'accounts'} · ${
    invitedCount === 0
      ? 'geen openstaande uitnodigingen'
      : `${invitedCount} openstaande uitnodiging${invitedCount === 1 ? '' : 'en'}`
  }`;

  return (
    <div className="grid gap-4">
      <Card
        title={
          <span className="flex items-baseline gap-2.5">
            {i18n('carrierPortal.users.users')}
            <span className="card-subtle-text font-normal">{meta}</span>
          </span>
        }
        rightHeading={
          <LinkButton to="invite" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
            {i18n('carrierPortal.users.inviteUser')}
          </LinkButton>
        }
        flush
      >
        <Table
          idKey="id"
          headers={accessHeaders}
          data={accessRows}
          emptyState={<EmptyState icon={<UsersIcon />} title={i18n('carrierPortal.users.noUsersFound')} />}
          mapData={(row) => {
            const { portalUser, invite } = row;

            if (invite) {
              return [
                <UserIdentityCell name={<span className="font-normal text-fg-muted">—</span>} />,
                invite.email,
                <Pill color={'warning' as PillColor}>{i18n('carrierPortal.users.invited')}</Pill>,
                null,
                null,
                <ConfirmDialog
                  triggerColor="ghost"
                  triggerText={<TrashIcon className="button-icon" />}
                  title={i18n('carrierPortal.users.removeInvite')}
                  submitText={i18n('carrierPortal.users.removeInvite')}
                  description={i18n('carrierPortal.users.confirmRemoveInvite', {
                    email: invite.email,
                  })}
                  onSubmit={async () => {
                    try {
                      await deleteCarrierUserInvite(supplier.id, invite.id);
                      refreshData();
                      toast.success(i18n('carrierPortal.users.inviteRemoved'));
                    } catch (err) {
                      captureException(err);
                      toast.error(
                        i18n('carrierPortal.users.couldNotRemoveInvite', {
                          error: getDisplayError(err),
                        }),
                      );
                    }
                  }}
                />,
              ];
            }

            const u = portalUser!;
            const isEditingNickname = editingNicknameId === u.id;
            const statusColor: PillColor = u.user.isActive ? 'positive' : 'negative';

            return [
              <UserIdentityCell name={u.user.name} />,
              u.user.email,
              <Pill color={statusColor}>
                {u.user.isActive ? i18n('carrierPortal.users.active') : i18n('carrierPortal.users.inactive')}
              </Pill>,
              u.user.isActive ? (
                <div onClick={(e) => e.stopPropagation()}>
                  {isEditingNickname ? (
                    <Input
                      type="text"
                      value={nicknameValue}
                      onChange={setNicknameValue}
                      onBlur={() => handleNicknameSubmit(u.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleNicknameSubmit(u.id);
                        } else if (e.key === 'Escape') {
                          setEditingNicknameId(null);
                        }
                      }}
                      placeholder="Bijnaam"
                    />
                  ) : (
                    <Input
                      type="text"
                      value={u.nickname ?? ''}
                      onChange={() => {}}
                      placeholder="-"
                      onFocus={() => {
                        setEditingNicknameId(u.id);
                        setNicknameValue(u.nickname ?? '');
                      }}
                    />
                  )}
                </div>
              ) : null,
              u.user.isActive ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <SimpleSelect
                    items={roleOptions}
                    selectedItem={roleOptions.find((r) => r.key === u.role) ?? null}
                    onSelect={(item) => {
                      if (item && item.key !== u.role) {
                        handleRoleChange(u.id, u.user.name, item.key);
                      }
                    }}
                  />
                </div>
              ) : null,
              null,
            ];
          }}
        />
      </Card>
    </div>
  );
};
